import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import ComponentPageHeader from '../../../Common/PageHeader/ComponentPageHeader';
import { TableColumns } from './utils/DataAnalystHeaderCell';
import DataTable from '../../../Common/DataTable';
import ComponentDeleteModal from '../../../Common/DeleteModal/ComponentDeleteModal';
import { AnalystData, DeleteAnalyticPayload } from './utils/TypeAnalyst';
import EndPoints from '../../../Routes/EndPoints';
import {
  deleteAnalyst, getAnalystData, onArchiveAnalyst, resetAnalystListState,
} from './redux/AnalystListSlice';
import { RootState } from '../../../redux/rootState';
import { setLoader } from '../../../redux/loaderSlice';
import { LocalizationContext } from '../../../Common/Localization/LocalizationProvider';
import { exportToExcelData } from '../../../Common/PageHeader/helper';
import ArchiveAnalystModal from './archiveAnalystModalComponent/ArchiveAnalystModal';
import {
  resetPagination, setPageNumber, setPageSize, setSearchParam,
  setSortColumn,
  setSortDirection,
} from '../../taiReportingSystem/redux/sliceReportWorkspace';

export default function AnalystListScreen() {
  const { translations } = useContext(LocalizationContext);
  const navigate = useNavigate();
  // const [page, setPage] = useState(1);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  // const [searchQuery, setSearchQuery] = useState('');
  const {
    pageNumber, pageSize, searchParam, sortColumn, sortDirection,
  } = useSelector((state: RootState) => state.reportWorkspaceReducer);
  const { historyStack } = useSelector((state:RootState) => state.masterDataReducer);
  const [showDeleteModal, setDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const dispatch = useDispatch();
  const { isLoading, analystData } = useSelector((state: RootState) => state.AnalystListSliceReducer);

  const handleDelete = (id: string) => {
    setDeleteModal(true);
    setSelectedId(id);
  };
  useEffect(() => {
    dispatch(setLoader(isLoading));
  }, [isLoading]);

  const handleDeleteModal = () => {
    const payload: DeleteAnalyticPayload = {
      analyticId: selectedId,
      page: pageNumber,
      rowsPerPage: pageSize,
      searchQuery: searchParam,
    };
    dispatch(deleteAnalyst(payload));
    setDeleteModal(false);
  };

  const handleEdit = (id: string) => {
    navigate(`${id}`);
  };

  const handleData = () => {
    const payload = {
      page: pageNumber,
      rowsPerPage: pageSize,
      searchQuery: searchParam,
      sortCol: sortColumn,
      sortDir: sortDirection,
    };
    dispatch(getAnalystData(payload));
  };

  useEffect(() => () => {
    dispatch(resetAnalystListState());
  }, []);

  useEffect(() => {
    handleData();
  }, [pageNumber, pageSize, searchParam, sortColumn, sortDirection]);

  const handleButtonClick = () => {
    navigate(EndPoints.ADD_ANALYST);
  };

  const tableData: AnalystData[] = useMemo(() => analystData?.Data?.map((item, index) => {
    const transformedDataItem = {

      sn: index,
      // action: 'edit&delete',
      action: 'edit&activeDeactivate',
      firstName: item.FirstName,
      lastName: item.LastName,
      baseCityName: item.BaseCityName,
      phone: item.Phone,
      cityName: item.CityName,
      state: item.State,
      zip: item.Zip,
      id: String(item?.AnalystId),
      isArchived: item?.IsArchived ? 'Archived' : 'Active',
    };
    return transformedDataItem;
  }), [analystData]);

  const excelData = useMemo(() => analystData?.Data?.map((item) => {
    const transformedDataItems = {
      FirstName: item.FirstName,
      LastName: item.LastName,
      BaseCity: item.BaseCityName,
      Phone: item.Phone,
      City: item.CityName,
      State: item.State,
      Zip: item.Zip,
      Archived: item?.IsArchived ? 'Yes' : 'No',
    };
    return transformedDataItems;
  }), [analystData]);

  const downloadExcel = () => {
    exportToExcelData(
      {
        data: excelData,
      },
      'Analyst_list',
    );
  };

  const handleActiveDeactivate = (id: string) => {
    setSelectedId(id);
    setShowArchiveModal(true);
  };
  const handleCallback = () => {
    setShowArchiveModal(false);
    handleData();
  };
  const handleArchiveModal = () => {
    if (selectedId) {
      const payload = {
        analystId: selectedId,
      };
      dispatch(onArchiveAnalyst({ payload, callback: handleCallback }));
    }
  };

  useEffect(() => {
    if (historyStack) {
      const finderString = '/analyst/';
      const last = historyStack[historyStack.length - 1];
      const sLast = historyStack[historyStack.length - 2];
      if (!(
        last?.includes(finderString)
          || (sLast?.includes(finderString) && last === '/analyst')
      )) {
        dispatch(resetPagination());
      }
    }
  }, [historyStack]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <ComponentPageHeader subHeading={translations.analystHeading} downloadExcel={downloadExcel} />
        </Grid>
        <Grid item xs={12} sm={12} md={12} className="dataTableCustomMargin">
          <DataTable<AnalystData>
            handleButtonClick={handleButtonClick}
            data={tableData}
            columns={TableColumns}
            totalRecords={analystData?.TotalRecords}
            buttonText="Add Analyst"
            page={pageNumber}
            setPage={(e) => { dispatch(setPageNumber(e)); }}
            rowsPerPage={pageSize}
            setRowsPerPage={(e) => { dispatch(setPageSize(e)); }}
            searchQuery={searchParam}
            setSearchQuery={(e) => { dispatch(setSearchParam(e)); dispatch(setPageNumber(1)); }}
            sortCol={sortColumn}
            setSortCol={(e) => { dispatch(setSortColumn(e)); dispatch(setPageNumber(1)); }}
            sortDir={sortDirection}
            setSortDir={(e) => { dispatch(setSortDirection(e)); dispatch(setPageNumber(1)); }}
            handleData={handleData}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
            handleActiveDeactivate={handleActiveDeactivate}
            handleDataFromComponent
          />
        </Grid>
      </Grid>
      <ComponentDeleteModal
        show={showDeleteModal}
        setOpenFrom={() => setDeleteModal(false)}
        handleDelete={handleDeleteModal}
      />
      {showArchiveModal
              && (
                <ArchiveAnalystModal
                  show={showArchiveModal}
                  setOpenFrom={() => setShowArchiveModal(false)}
                  handleDeactivate={handleArchiveModal}
                  rowId={selectedId}
                />
              )}

    </>
  );
}
